import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Clock, Calendar, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { authFetch, API } from '../utils/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Reports({ user }) {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [preset, setPreset] = useState('7days');

  useEffect(() => {
    fetchReportsData();
  }, []);

  const fetchReportsData = async (startDate = null, endDate = null) => {
    try {
      let summaryUrl = `${API}/reports/summary`;
      let historyUrl = `${API}/time/history?limit=100`;

      if (startDate && endDate) {
        const params = `start_date=${startDate}&end_date=${endDate}`;
        summaryUrl += `?${params}`;
        historyUrl += `&${params}`;
      }

      const [summaryRes, historyRes] = await Promise.all([
        authFetch(summaryUrl),
        authFetch(historyUrl)
      ]);

      const summaryData = await summaryRes.json();
      const historyData = await historyRes.json();

      setSummary(summaryData);
      setHistory(historyData);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (presetType) => {
    setPreset(presetType);
    const today = new Date();
    let startDate = new Date();

    switch (presetType) {
      case '7days':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'week':
        startDate.setDate(today.getDate() - today.getDay());
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    const start = startDate.toISOString().split('T')[0];
    const end = today.toISOString().split('T')[0];
    setDateRange({ start, end });
    fetchReportsData(start + 'T00:00:00Z', end + 'T23:59:59Z');
  };

  const applyCustomRange = () => {
    if (!dateRange.start || !dateRange.end) {
      toast.error('Please select both start and end dates');
      return;
    }
    setPreset('custom');
    fetchReportsData(dateRange.start + 'T00:00:00Z', dateRange.end + 'T23:59:59Z');
  };

  const handleExport = async () => {
    try {
      let exportUrl = `${BACKEND_URL}/api/time/export`;
      if (dateRange.start && dateRange.end) {
        exportUrl += `?start_date=${dateRange.start}T00:00:00Z&end_date=${dateRange.end}T23:59:59Z`;
      }

      const response = await authFetch(exportUrl);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `timesheet_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Report exported successfully!');
      } else {
        toast.error('Failed to export report');
      }
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const getChartData = () => {
    const dateGroups = {};

    history.forEach((entry) => {
      if (entry.duration) {
        const date = new Date(entry.clock_in);
        const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (!dateGroups[dateStr]) {
          dateGroups[dateStr] = 0;
        }
        dateGroups[dateStr] += entry.duration / 60;
      }
    });

    return Object.keys(dateGroups).map((date) => ({
      date,
      hours: parseFloat(dateGroups[date].toFixed(2))
    }));
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-foreground-muted">Loading...</p>
        </div>
      </Layout>
    );
  }

  const chartData = getChartData();

  return (
    <Layout user={user}>
      <div data-testid="reports-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT">Reports & Analytics</h1>
          <Button
            onClick={handleExport}
            className="bg-accent hover:bg-accent/90 rounded-full"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6 mb-6" data-testid="date-range-filter">
          <h2 className="text-lg font-heading font-bold text-foreground-DEFAULT mb-4">Filter by Date Range</h2>
          
          <div  className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => applyPreset('7days')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                preset === '7days'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-foreground-DEFAULT hover:bg-slate-200'
              }`}
              data-testid="preset-7days"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => applyPreset('week')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                preset === 'week'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-foreground-DEFAULT hover:bg-slate-200'
              }`}
              data-testid="preset-week"
            >
              This Week
            </button>
            <button
              onClick={() => applyPreset('month')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                preset === 'month'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-foreground-DEFAULT hover:bg-slate-200'
              }`}
              data-testid="preset-month"
            >
              This Month
            </button>
            <button
              onClick={() => applyPreset('year')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                preset === 'year'
                  ? 'bg-primary text-white'
                  : 'bg-slate-100 text-foreground-DEFAULT hover:bg-slate-200'
              }`}
              data-testid="preset-year"
            >
              This Year
            </button>
          </div>

          <div  className="flex flex-wrap items-end gap-3">
            <div  className="flex-1 min-w-[150px]">
              <label  className="block text-sm font-medium text-foreground-DEFAULT mb-2">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                 className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                data-testid="start-date"
              />
            </div>
            <div  className="flex-1 min-w-[150px]">
              <label  className="block text-sm font-medium text-foreground-DEFAULT mb-2">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                 className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                data-testid="end-date"
              />
            </div>
            <Button
              onClick={applyCustomRange}
               className="bg-primary hover:bg-primary-hover rounded-full"
              data-testid="apply-custom-range"
            >
              Apply Range
            </Button>
          </div>
        </div>

        <div  className="space-y-6">
          <div  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {user.role === 'admin' ? (
              <>
                <div  className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <div  className="flex items-center gap-3 mb-3">
                    <div  className="bg-primary/10 rounded-xl p-3">
                      <TrendingUp  className="w-6 h-6 text-primary" />
                    </div>
                    <h3  className="font-semibold text-foreground-DEFAULT">Total Employees</h3>
                  </div>
                  <p  className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                    {summary?.total_employees || 0}
                  </p>
                </div>

                <div  className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <div  className="flex items-center gap-3 mb-3">
                    <div  className="bg-accent/10 rounded-xl p-3">
                      <Clock  className="w-6 h-6 text-accent" />
                    </div>
                    <h3  className="font-semibold text-foreground-DEFAULT">Active Today</h3>
                  </div>
                  <p  className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                    {summary?.active_today || 0}
                  </p>
                </div>

                <div  className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <div  className="flex items-center gap-3 mb-3">
                    <div  className="bg-orange-100 rounded-xl p-3">
                      <Calendar  className="w-6 h-6 text-orange-600" />
                    </div>
                    <h3  className="font-semibold text-foreground-DEFAULT">Pending Leaves</h3>
                  </div>
                  <p  className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                    {summary?.pending_leaves || 0}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div  className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <div  className="flex items-center gap-3 mb-3">
                    <div  className="bg-primary/10 rounded-xl p-3">
                      <TrendingUp  className="w-6 h-6 text-primary" />
                    </div>
                    <h3  className="font-semibold text-foreground-DEFAULT">Total Hours</h3>
                  </div>
                  <p  className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                    {summary?.total_hours || 0}h
                  </p>
                </div>

                <div  className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
                  <div  className="flex items-center gap-3 mb-3">
                    <div  className="bg-accent/10 rounded-xl p-3">
                      <Calendar  className="w-6 h-6 text-accent" />
                    </div>
                    <h3  className="font-semibold text-foreground-DEFAULT">Total Entries</h3>
                  </div>
                  <p  className="text-3xl font-heading font-bold text-foreground-DEFAULT">
                    {summary?.total_entries || 0}
                  </p>
                </div>
              </>
            )}
          </div>

          <div  className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-testid="chart-container">
            <h2  className="text-xl font-heading font-bold text-foreground-DEFAULT mb-6">Time Activity</h2>
            {chartData.length === 0 ? (
              <p  className="text-foreground-muted text-center py-12">No data for selected period</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#64748b" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="hours" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Reports;
